import { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import loanDocumentsApi from '../../../services/loanDocumentsApi';
import { baseApi } from '../../../services/baseApi';
import StatusBadge, { DOCUMENT_STATUS_MAP } from '../../../components/shared/StatusBadge';
import { env } from '../../../config/env';
import FileUploadModal from '../../../components/shared/FileUploadModal';
import LoadingSkeleton from '../../../components/shared/LoadingSkeleton';
import EmptyState from '../../../components/shared/EmptyState';

const STATUS_TABS = [
  { key: 'Uploaded', label: 'Uploaded', color: 'warning' },
  { key: 'Reviewed', label: 'Reviewed', color: 'info' },
  { key: 'Accepted', label: 'Accepted', color: 'success' },
];

const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const ALLOWED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg';
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB

// ── helpers ──

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

// NOTE: UploadModal is now imported as FileUploadModal from shared/

const getAuthHeaders = (token) => ({
  Authorization: token ? `Bearer ${token}` : '',
});

const buildDownloadUrl = (loanId, documentId) =>
  `${env.apiUrl}/loans/${loanId}/documents/${documentId}/download`;

// ── PDF Viewer Modal ──

const PdfViewerModal = ({ show, onClose, pdfUrl, docName, loading: viewLoading }) => {
  if (!show) return null;
  return (
    <Dialog open={show} onClose={onClose} maxWidth={false} PaperProps={{ sx: { width: '90vw', maxWidth: 900, height: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {docName || 'Document Preview'}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
        {viewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
        ) : pdfUrl ? (
          <iframe src={pdfUrl} title="Document Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="text.secondary">Unable to load document preview.</Typography></Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main DocumentsTab ──

const DocumentsTab = ({ loanId, disableMutations = false }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { data, isLoading, isFetching, error, refetch } = loanDocumentsApi.endpoints.getLoanDocuments.useQuery(loanId, { skip: !loanId });
  const [reuploadDoc] = loanDocumentsApi.endpoints.reuploadLoanDocument.useMutation();

  const [activeStatus, setActiveStatus] = useState('Uploaded');
  const [uploadError, setUploadError] = useState(null);
  const [reuploadingId, setReuploadingId] = useState(null);
  const reuploadInputRef = useRef(null);
  const reuploadDocIdRef = useRef(null);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // PDF viewer state
  const [viewerDoc, setViewerDoc] = useState(null); // { name, id }
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // ── Upload selected files in a single backend request ──
  const uploadFilesBatch = async (files) => {
    if (!files?.length) return;
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    await axios.post(
      `${env.apiUrl}/loans/${loanId}/documents`,
      formData,
      {
        headers: { ...getAuthHeaders(token) },
      }
    );
  };

  // Called when all parallel uploads finish — refresh the documents list
  const handleAllUploadsComplete = () => {
    dispatch(baseApi.util.invalidateTags([{ type: 'LoanDocuments', id: loanId }]));
  };

  // ── Re-upload ──
  const handleReupload = async (e, documentId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReuploadingId(documentId);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await reuploadDoc({ loanId, documentId, formData }).unwrap();
    } catch (err) {
      setUploadError(err.data?.message || err.data || 'Re-upload failed.');
    } finally {
      setReuploadingId(null);
      if (reuploadInputRef.current) reuploadInputRef.current.value = '';
    }
  };

  // ── Authenticated download as blob ──
  const handleDownload = async (documentId, fileName) => {
    try {
      const resp = await axios.get(buildDownloadUrl(loanId, documentId), {
        headers: { ...getAuthHeaders(token) },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setUploadError('Download failed.');
    }
  };

  // ── View PDF in modal ──
  const handleView = async (doc) => {
    // Clean up previous blob URL
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setViewerDoc({ name: doc.nameOverride || doc.documentTypeName || 'Document', id: doc.id });
    setViewLoading(true);
    try {
      const resp = await axios.get(buildDownloadUrl(loanId, doc.id), {
        headers: { ...getAuthHeaders(token) },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      setPdfBlobUrl(url);
    } catch {
      setPdfBlobUrl(null);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewer = () => {
    setViewerDoc(null);
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
  };

  // ── Render ──

  if (isLoading) {
    return <LoadingSkeleton variant="table" rows={6} />;
  }

  if (error) {
    return <Alert severity="error">Failed to load documents. {error.data?.message || error.data || ''}</Alert>;
  }

  const allDocs = data?.documents || [];
  const filteredDocs = allDocs.filter(d => d.status === activeStatus);
  const countByStatus = (status) => allDocs.filter(d => d.status === status).length;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={700}>Documents ({allDocs.length})</Typography>
          <IconButton size="small" onClick={refetch} disabled={isFetching} title="Refresh documents">
            {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Stack>
        {!disableMutations && (
          <Button variant="contained" startIcon={<UploadIcon />} onClick={() => { setUploadError(null); setShowUploadModal(true); }}>Upload Document(s)</Button>
        )}
      </Stack>

      {uploadError && !showUploadModal && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 2 }}>{uploadError}</Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STATUS_TABS.map(({ key, label, color }) => (
          <Grid key={key} size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h4" color={`${color}.main`}>{countByStatus(key)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status Tabs */}
      <Tabs value={activeStatus} onChange={(_, v) => setActiveStatus(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {STATUS_TABS.map(({ key, label, color }) => (
          <Tab key={key} value={key} label={<Stack direction="row" spacing={0.75} alignItems="center">{label}<Chip label={countByStatus(key)} size="small" color={color} variant="outlined" /></Stack>} />
        ))}
      </Tabs>

      {/* Documents Table */}
      {filteredDocs.length === 0 ? (
        <EmptyState
          icon={<DescriptionIcon />}
          title={`No ${activeStatus.toLowerCase()} documents`}
          description={allDocs.length === 0 ? 'Upload documents to get started.' : `No documents are currently in "${activeStatus}" status.`}
          action={allDocs.length === 0 && !disableMutations && (
            <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setShowUploadModal(true)}>Upload First Document(s)</Button>
          )}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Files</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ width: 120 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocs.map(doc => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                reuploadingId={reuploadingId}
                reuploadInputRef={reuploadInputRef}
                reuploadDocIdRef={reuploadDocIdRef}
                onReupload={handleReupload}
                onDownload={handleDownload}
                onView={handleView}
                disableReupload={disableMutations}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {/* Upload Modal */}
      <FileUploadModal
        show={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        uploadBatchFn={uploadFilesBatch}
        onAllComplete={handleAllUploadsComplete}
        title="Upload Documents"
        confirmLabel="Upload All"
        accept={ALLOWED_EXTENSIONS}
        allowedTypes={ALLOWED_TYPES}
        maxSize={MAX_FILE_SIZE}
        hint="PDF, PNG, JPEG — max 200 MB per file"
      />

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        show={!!viewerDoc}
        onClose={closeViewer}
        pdfUrl={pdfBlobUrl}
        docName={viewerDoc?.name}
        loading={viewLoading}
      />
    </Box>
  );
};

// ── DocumentRow ──

const DocumentRow = ({ doc, reuploadingId, reuploadInputRef, reuploadDocIdRef, onReupload, onDownload, onView, disableReupload = false }) => {
  const isUploaded = doc.status === 'Uploaded';
  const isReuploading = reuploadingId === doc.id;
  const totalSize = doc.files?.reduce((sum, f) => sum + (f.fileSize || 0), 0) || 0;
  const fileName = doc.nameOverride || doc.documentTypeName || 'document';

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <DescriptionIcon fontSize="small" color="action" />
          <Box>
            <Typography variant="body2" fontWeight={700}>{fileName}</Typography>
            {totalSize > 0 && <Typography variant="caption" color="text.secondary">{formatFileSize(totalSize)}</Typography>}
          </Box>
        </Stack>
      </TableCell>
      <TableCell>{doc.category ? <Chip label={doc.category} size="small" variant="outlined" color="info" /> : '—'}</TableCell>
      <TableCell>
        {doc.uploadedTimestamp ? (
          <>
            <Typography variant="body2" color="text.secondary">{new Date(doc.uploadedTimestamp).toLocaleDateString()}</Typography>
            <Typography variant="caption" color="text.secondary">{new Date(doc.uploadedTimestamp).toLocaleTimeString()}</Typography>
          </>
        ) : '—'}
      </TableCell>
      <TableCell>
        {doc.files?.length > 0 ? <Chip icon={<AttachFileIcon />} label={doc.files.length} size="small" variant="outlined" color="info" /> : '—'}
      </TableCell>
      <TableCell>
        <StatusBadge status={doc.status} map={DOCUMENT_STATUS_MAP} />
        {doc.isExpired && <StatusBadge variant="danger" className="ms-1">Expired</StatusBadge>}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" title="View" onClick={() => onView(doc)}><VisibilityIcon fontSize="small" /></IconButton>
          <IconButton size="small" title="Download" onClick={() => onDownload(doc.id, fileName)}><DownloadIcon fontSize="small" /></IconButton>
          {isUploaded && !disableReupload && (
            <>
              <IconButton size="small" title="Re-upload" disabled={isReuploading} onClick={() => { reuploadDocIdRef.current = doc.id; reuploadInputRef.current?.click(); }}>
                {isReuploading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
              <input ref={reuploadInputRef} type="file" style={{ display: 'none' }} accept={ALLOWED_EXTENSIONS} onChange={(e) => { onReupload(e, reuploadDocIdRef.current); }} />
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default DocumentsTab;
